"use client";

import { useEffect, useState, useRef } from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import { ThemeName } from "./ThemeModeScript";

interface ThemeSwitcherProps {
  variant?: "pill" | "menu";
}

export function ThemeSwitcher({ variant = "menu" }: ThemeSwitcherProps) {
  const [mounted, setMounted] = useState(false);
  const [theme, setTheme] = useState<ThemeName>("system");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const SK = "meshflow.theme-preference";
    const s = window.localStorage;
    const pref = JSON.parse(s[SK] || "{}");
    if (pref.theme) {
      setTheme(pref.theme as ThemeName);
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    const handleStorage = (e: StorageEvent) => {
      if (e.key === SK) {
        const stored = JSON.parse(s[window.localStorage[SK] || "{}"]);
        if (stored?.theme) {
          setTheme(stored.theme as ThemeName);
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!mounted) {
    if (variant === "pill")
      return (
        <div className="h-9 w-[120px] rounded-full bg-muted/20 animate-pulse" />
      );
    return (
      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-muted/20 animate-pulse" />
    );
  }

  const setThemeValue = (newTheme: ThemeName) => {
    setTheme(newTheme);
    const SK = "meshflow.theme-preference";
    const s = window.localStorage;
    const currentPrefs = JSON.parse(s.getItem(SK) || "{}");
    const newPrefsString = JSON.stringify({ ...currentPrefs, theme: newTheme });
    s.setItem(SK, newPrefsString);

    window.__themeRefresh?.();
    setMenuOpen(false);
  };

  if (variant === "pill") {
    return (
      <div className="flex items-center gap-1 p-1 rounded-full bg-muted/30 border border-border inline-flex w-fit">
        <button
          onClick={() => setThemeValue("light")}
          className={`p-1.5 rounded-full transition-all flex items-center justify-center ${theme === "light" ? "bg-background shadow-md text-foreground ring-1 ring-border dark:bg-muted-foreground/20 dark:ring-white/10" : "text-muted-foreground hover:text-foreground"}`}
          aria-label="Light theme"
        >
          <Sun className="w-4 h-4" />
        </button>
        <button
          onClick={() => setThemeValue("system")}
          className={`p-1.5 rounded-full transition-all flex items-center justify-center ${theme === "system" ? "bg-background shadow-md text-foreground ring-1 ring-border dark:bg-muted-foreground/20 dark:ring-white/10" : "text-muted-foreground hover:text-foreground"}`}
          aria-label="System theme"
        >
          <Monitor className="w-4 h-4" />
        </button>
        <button
          onClick={() => setThemeValue("dark")}
          className={`p-1.5 rounded-full transition-all flex items-center justify-center ${theme === "dark" ? "bg-background shadow-md text-foreground ring-1 ring-border dark:bg-muted-foreground/20 dark:ring-white/10" : "text-muted-foreground hover:text-foreground"}`}
          aria-label="Dark theme"
        >
          <Moon className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const currentIcon = {
    light: <Sun className="w-4 h-4 text-foreground" />,
    dark: <Moon className="w-4 h-4 text-foreground" />,
    system: <Monitor className="w-4 h-4 text-foreground/70" />,
  }[theme];

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        className="p-2 rounded-full hover:bg-muted/50 transition-colors flex items-center justify-center border border-border/50 bg-background shadow-sm w-9 h-9"
        aria-label="Theme menu"
        title={`Current theme: ${theme}`}
      >
        {currentIcon}
      </button>

      {menuOpen && (
        <div
          className="absolute right-0 mt-2 p-1 w-32 rounded-xl bg-card border border-border shadow-lg z-50 flex flex-col gap-1 overflow-hidden"
          style={{ top: "100%" }}
        >
          <button
            className={`flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg transition-colors ${theme === "light" ? "bg-muted font-medium text-foreground" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"}`}
            onClick={() => setThemeValue("light")}
          >
            <Sun className="w-4 h-4" />
            <span>Light</span>
          </button>
          <button
            className={`flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg transition-colors ${theme === "dark" ? "bg-muted font-medium text-foreground" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"}`}
            onClick={() => setThemeValue("dark")}
          >
            <Moon className="w-4 h-4" />
            <span>Dark</span>
          </button>
          <button
            className={`flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg transition-colors ${theme === "system" ? "bg-muted font-medium text-foreground" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"}`}
            onClick={() => setThemeValue("system")}
          >
            <Monitor className="w-4 h-4" />
            <span>System</span>
          </button>
        </div>
      )}
    </div>
  );
}
